import { type BundleQuestionFragment, match } from "@stepapp/core";
import { AttributeType, V2CartQuestionsUpsert } from "@stepapp/core/apiTypes";
import { Block, Divider, useBreakpoints } from "@stepapp/ui";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import type { ReservationFormParams } from "../../store/draftReservation/types";

import ErrorMessage from "../ErrorMessage";
import { Grid } from "../Grid";
import { ServiceSceneDescription, ServiceSceneHeader } from "./ServiceSceneHeader";
import SimpleAnswerItem from "./SimpleAnswerItem";

type Props = {
  questions: BundleQuestionFragment[];
  noDivider?: boolean;
};

const BasicQuestions = ({ noDivider, questions }: Props) => {
  const { md } = useBreakpoints();
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ReservationFormParams>();

  const attributes = watch("attributes");

  const setNewAttribute = useCallback(
    (attributes: V2CartQuestionsUpsert[]) => {
      setValue("attributes", attributes, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  return (
    <Block>
      {questions
        .concat()
        .sort((a, b) => a.position - b.position)
        .map(({ id: questionId, name, description, answers, isRequired, type, numericType }) => {
          const gap = md ? 16 : 8;
          const oneColumn = type === AttributeType.Numeric;
          const errorMsg =
            !!errors?.attributes?.message && !attributes.some((a) => a?.attributeId === questionId)
              ? errors?.attributes?.message
              : "";
          const maxtNumberOfCharacters = Math.max(...answers.map((answer) => answer?.name?.length));

          const maxColumns = match(maxtNumberOfCharacters)
            .when(
              (v) => v > 30,
              () => 1,
            )
            .when(
              (v) => v > 21,
              () => 2,
            )
            .when(
              (v) => v > 12,
              () => 3,
            )
            .otherwise(() => 4);
          const numberOfOptions = Math.min(answers?.length, maxColumns);

          return (
            <Block
              marginTop={1}
              id={questionId}
              key={questionId}
              testID={`question-id-${questionId}`}
            >
              <ServiceSceneHeader name={name} isRequired={!!isRequired} />

              <ServiceSceneDescription description={description || ""} />

              <Block marginTop={3}>
                <Grid gap={gap} numberOfOptions={numberOfOptions} oneColumn={oneColumn}>
                  {answers
                    .concat()
                    .sort((a, b) => a.position - b.position)
                    .map(({ id, name, description, isFeatured }) => {
                      const isSelected = !!attributes?.find((a) => a?.valueId?.includes(id));
                      return (
                        <Block key={id}>
                          <SimpleAnswerItem
                            description={description}
                            id={id}
                            isFeatured={isFeatured}
                            setNewAttribute={setNewAttribute}
                            questionType={type}
                            questionId={questionId}
                            selected={isSelected}
                            title={name}
                            attributes={attributes}
                            numericType={numericType}
                          />
                        </Block>
                      );
                    })}
                </Grid>
                <ErrorMessage message={errorMsg} />
              </Block>

              {!noDivider && (
                <Block marginTop={1}>
                  <Divider />
                </Block>
              )}
            </Block>
          );
        })}
    </Block>
  );
};

export default BasicQuestions;
